import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

import type { ActorHandle, ActorInspection, ActorLoadReceipt, ActorSnapshot } from './actorPipeline';

export type RouteCue = Readonly<{
  id: 'cinderwell-route-07';
  pose: 'signal' | 'hold' | 'depart';
  worldPosition: readonly [number, number, number];
  worldYaw: number;
  boardVisible: boolean;
  routeLineState: 'dim' | 'active';
}>;

export type RouteScoutLifecycle = 'active' | 'hit' | 'dying' | 'dead';

export type RouteScoutSnapshot = Readonly<{
  lifecycle: RouteScoutLifecycle;
  elapsedMs: number;
  reducedMotion: boolean;
  cue: RouteCue;
}>;

export type RouteScoutInspection = Readonly<{
  presentation: 'integrated-mesh';
  visibleFullActorPlanes: number;
  castShadowMeshes: number;
  receiveShadowMeshes: number;
  hasGroundContact: boolean;
  materialProfile: 'arcos-painterly-matte';
  boardPresentation: 'world-route-markers';
}>;

export interface RouteScoutHandle extends ActorHandle {
  readonly id: 'route-scout';
  setRouteCue(cue: RouteCue): void;
  inspect(): ActorInspection & Readonly<{ boardPresentation: 'world-route-markers' }>;
}

const textureCache = new Map<string, THREE.CanvasTexture>();
function fibreMap(base: string) {
  const cached = textureCache.get(base);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 96;
  const context = canvas.getContext('2d')!;
  context.fillStyle = base;
  context.fillRect(0, 0, 96, 96);
  let seed = 1707;
  const random = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let index = 0; index < 180; index += 1) {
    const alpha = .05 + random() * .14;
    context.fillStyle = `rgba(220,198,162,${alpha})`;
    context.fillRect(random() * 96, random() * 96, 1 + random() * 8, 1 + random() * 2);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2.4, 2.4);
  textureCache.set(base, texture);
  return texture;
}

function material(color: number, base: string, roughness = .94, metalness = .02, emissive = 0, emissiveIntensity = 0) {
  const ambientLift = new THREE.Color(0x7e8881).lerp(new THREE.Color(color), .42).getHex();
  return new THREE.MeshStandardMaterial({
    color,
    map: fibreMap(base),
    roughness,
    metalness,
    emissive: emissive || ambientLift,
    // Keep authored colour in the shadows without turning props into self-lit cut-outs.
    // The scene key/fill and fog must remain responsible for the final value structure.
    emissiveIntensity: emissive ? emissiveIntensity : .18,
    toneMapped: true,
    fog: true,
  });
}

function solid(geometry: THREE.BufferGeometry, actorMaterial: THREE.Material, name: string) {
  const object = new THREE.Mesh(geometry, actorMaterial);
  object.name = name;
  object.castShadow = true;
  object.receiveShadow = true;
  return object;
}

function createContactDecal() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const context = canvas.getContext('2d')!;
  // Bias the dense core beneath the boots and let the soft cast tail travel away from
  // Cinderwell's upper-left key. This avoids the previous symmetrical "hover disc".
  const gradient = context.createRadialGradient(78, 64, 3, 126, 64, 122);
  gradient.addColorStop(0, 'rgba(5,7,8,.92)');
  gradient.addColorStop(.34, 'rgba(8,10,11,.58)');
  gradient.addColorStop(.68, 'rgba(10,12,13,.22)');
  gradient.addColorStop(1, 'rgba(12,14,15,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 256, 128);
  const texture = new THREE.CanvasTexture(canvas);
  const decal = new THREE.Mesh(
    new THREE.PlaneGeometry(1.84, .72),
    new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: .99, depthWrite: false, toneMapped: true }),
  );
  decal.name = 'groundAnchor-contact-decal';
  decal.rotation.x = -Math.PI / 2;
  decal.rotation.z = -.24;
  decal.position.set(.2, .018, .06);
  decal.renderOrder = 2;
  return decal;
}

function createBoard(routeMaterial: THREE.MeshStandardMaterial) {
  const board = new THREE.Group();
  board.name = 'backBoard-socket';
  // Compact shoulder-slung field board. It overlaps the scout silhouette and is tied back
  // into the shoulder/belt, so route information reads as equipment rather than a sign card.
  board.position.set(-.38, 1.12, .03);
  board.rotation.set(-.08, -.18, -.18);
  board.scale.setScalar(.58);

  const face = solid(new THREE.BoxGeometry(.82, 1.08, .075), material(0x99866c, '#75634e', .92), 'route-board-solid');
  board.add(face);
  const frameMaterial = material(0x69422f, '#684331', .7, .32);
  for (const x of [-.43, .43]) {
    const rail = solid(new THREE.BoxGeometry(.065, 1.18, .12), frameMaterial, 'route-board-frame');
    rail.position.set(x, 0, .005);
    board.add(rail);
  }
  for (const y of [-.57, .57]) {
    const rail = solid(new THREE.BoxGeometry(.92, .065, .12), frameMaterial, 'route-board-frame');
    rail.position.set(0, y, .005);
    board.add(rail);
  }

  const routeLine = new THREE.Group();
  routeLine.name = 'routeLine-socket';
  routeLine.position.z = .052;
  const points = [
    new THREE.Vector3(-.27, -.4, 0), new THREE.Vector3(-.12, -.22, 0),
    new THREE.Vector3(-.18, .02, 0), new THREE.Vector3(.09, .18, 0),
    new THREE.Vector3(.02, .39, 0), new THREE.Vector3(.28, .47, 0),
  ];
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index];
    const end = points[index + 1];
    const delta = end.clone().sub(start);
    const segment = solid(new THREE.CylinderGeometry(.018, .022, delta.length(), 7), routeMaterial, 'route-rust-line');
    segment.position.copy(start).add(end).multiplyScalar(.5);
    segment.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.clone().normalize());
    routeLine.add(segment);
  }
  for (const point of [points[0], points[points.length - 1]]) {
    const marker = solid(new THREE.CylinderGeometry(.052, .052, .018, 10), routeMaterial, 'route-marker');
    marker.position.copy(point);
    marker.rotation.x = Math.PI / 2;
    routeLine.add(marker);
  }
  board.add(routeLine);
  const slingMaterial = material(0x3a2d28, '#362a27', .97, .03);
  const sling = new THREE.Group();
  sling.name = 'route-board-shoulder-sling';
  for (const [start, end] of [
    [new THREE.Vector3(-.04, 1.55, -.01), new THREE.Vector3(-.34, 1.43, .02)],
    [new THREE.Vector3(-.02, .7, -.01), new THREE.Vector3(-.36, .82, .02)],
  ] as const) {
    const delta = end.clone().sub(start);
    const strap = solid(new THREE.CylinderGeometry(.018, .018, delta.length(), 6), slingMaterial, 'route-board-sling-strap');
    strap.position.copy(start).add(end).multiplyScalar(.5);
    strap.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.clone().normalize());
    sling.add(strap);
  }
  return { board, routeLine, sling };
}

function createIdentityKit(routeMaterial: THREE.MeshStandardMaterial) {
  const identity = new THREE.Group();
  identity.name = 'arcos-scout-identity-kit';
  const clothTeal = material(0x416f69, '#41645f');
  const clothDark = material(0x292838, '#343142');
  const ceramic = material(0xc8bca7, '#b7aa93', .9);
  const copper = material(0x81503a, '#714633', .58, .52);

  const mantle = new THREE.Group();
  mantle.name = 'mantle-socket';
  const shoulder = solid(new THREE.ConeGeometry(.59, .48, 7), clothDark, 'scout-mantle');
  shoulder.position.set(0, 1.38, -.01);
  shoulder.rotation.y = Math.PI / 7;
  shoulder.scale.z = .76;
  mantle.add(shoulder);
  const scarf = solid(new THREE.TorusGeometry(.29, .085, 6, 13, Math.PI * 1.68), clothTeal, 'scout-scarf');
  scarf.position.set(0, 1.48, .02);
  scarf.rotation.x = Math.PI / 2;
  mantle.add(scarf);

  const mask = new THREE.Group();
  mask.name = 'mask-socket';
  mask.position.set(0, 1.69, .13);
  const face = solid(new THREE.SphereGeometry(.225, 16, 10), ceramic, 'scout-ceramic-mask');
  face.scale.set(.87, 1.08, .42);
  mask.add(face);
  const sight = solid(new THREE.BoxGeometry(.31, .032, .03), clothDark, 'scout-mask-sight');
  sight.position.set(.015, .025, .095);
  sight.rotation.z = -.16;
  mask.add(sight);
  const routeGlyph = solid(new THREE.TorusGeometry(.16, .014, 5, 16, Math.PI * 1.18), routeMaterial, 'scout-route-glyph');
  routeGlyph.position.set(-.01, -.01, .108);
  routeGlyph.rotation.z = -.53;
  mask.add(routeGlyph);

  const staff = solid(new THREE.CylinderGeometry(.026, .035, 1.92, 8), copper, 'rightHandStaff-socket');
  staff.position.set(.34, .9, .01);
  staff.rotation.z = -.14;
  const finial = solid(new THREE.OctahedronGeometry(.105, 0), routeMaterial, 'route-staff-finial');
  finial.position.set(.47, 1.84, .01);

  identity.add(mantle, mask, staff, finial);
  return { identity, mantle, mask };
}

/** The donor URL is injected so body art can be swapped without changing route-cue,
 * board, identity-kit, fallback, lifecycle, or encounter composition code. */
export function createRouteScout(modelUrl: string): RouteScoutHandle {
  const root = new THREE.Group();
  root.name = 'route-scout-world-root';
  const bodySocket = new THREE.Group();
  bodySocket.name = 'scout-body-socket';
  const fallback = new THREE.Group();
  fallback.name = 'scout-dimensional-fallback';
  const routeMaterial = material(0xa54534, '#8b392d', .73, .08, 0x4d110b, .35);
  const { identity, mantle, mask } = createIdentityKit(routeMaterial);
  const { board, routeLine, sling } = createBoard(routeMaterial);
  const groundContact = createContactDecal();

  const fallbackCloth = material(0x354c4b, '#455d58');
  const fallbackDark = material(0x252a32, '#30343c');
  const torso = solid(new THREE.CylinderGeometry(.27, .36, 1.03, 8), fallbackCloth, 'fallback-torso');
  torso.position.y = .94;
  torso.scale.z = .72;
  fallback.add(torso);
  for (const x of [-.2, .2]) {
    const leg = solid(new THREE.CapsuleGeometry(.07, .57, 4, 7), fallbackDark, 'fallback-leg');
    leg.position.set(x, .34, 0);
    fallback.add(leg);
    const arm = solid(new THREE.CapsuleGeometry(.065, .54, 4, 7), fallbackCloth, 'fallback-arm');
    arm.position.set(x * 1.55, .93, .01);
    arm.rotation.z = x > 0 ? -.28 : .28;
    fallback.add(arm);
  }
  const hood = solid(new THREE.SphereGeometry(.27, 13, 9), fallbackDark, 'fallback-hood');
  hood.position.set(0, 1.62, -.02);
  hood.scale.set(1, 1.1, .82);
  fallback.add(hood);
  const cape = solid(new THREE.ConeGeometry(.46, 1.18, 9, 1, false), fallbackDark, 'fallback-field-cape');
  cape.position.set(0, .88, -.13); cape.scale.z = .58; fallback.add(cape);
  const belt = solid(new THREE.TorusGeometry(.29, .045, 6, 12), material(0x76503b, '#634230', .62, .42), 'fallback-belt');
  belt.position.set(0, .68, .02); belt.rotation.x = Math.PI / 2; fallback.add(belt);

  root.add(bodySocket, fallback, identity, board, sling, groundContact);
  // Settle animated soles into the shared ground plane instead of leaving a bright gap.
  bodySocket.position.y = -.64;
  fallback.position.y = -.64;
  identity.position.y = -.64;
  root.position.set(-2.55, 0, -4.15);
  root.rotation.y = 0;
  root.scale.setScalar(1.06);

  let disposed = false;
  let mixer: THREE.AnimationMixer | undefined;
  let importedLoaded = false;
  let receipt: ActorLoadReceipt = { sourceId: 'scout-body', usedFallback: true, meshCount: 0, skinnedMeshCount: 0, animationNames: [] };
  const ready = new Promise<ActorLoadReceipt>((resolve) => {
    new GLTFLoader().load(modelUrl, (gltf) => {
      if (disposed) { resolve(receipt); return; }
      const imported = gltf.scene;
      let meshCount = 0;
      let skinnedMeshCount = 0;
      imported.traverse((item) => {
        if (!(item instanceof THREE.Mesh)) return;
        meshCount += 1;
        if (item instanceof THREE.SkinnedMesh) skinnedMeshCount += 1;
        item.castShadow = true;
        item.receiveShadow = true;
        const materials = Array.isArray(item.material) ? item.material : [item.material];
        const finished = materials.map(value => {
          if (!(value instanceof THREE.MeshStandardMaterial)) return value;
          const result = value.clone(); const sourceColor = value.color.clone(); const hsl = sourceColor.getHSL({ h: 0, s: 0, l: 0 });
          result.color = sourceColor.offsetHSL(0, -.04, .045).lerp(new THREE.Color(hsl.l < .18 ? 0x426b67 : 0xc8b18f), hsl.l < .18 ? .5 : .22); const hex = `#${result.color.getHexString()}`;
          result.map = fibreMap(hex); result.roughness = Math.max(.65, value.roughness);
          result.emissive = new THREE.Color(0x87958d).lerp(result.color, .42); result.emissiveIntensity = .15;
          result.fog = true; result.toneMapped = true; result.needsUpdate = true; return result;
        });
        item.material = Array.isArray(item.material) ? finished : finished[0];
      });
      if (meshCount === 0) { resolve(receipt); return; }
      const bounds = new THREE.Box3().setFromObject(imported);
      const size = bounds.getSize(new THREE.Vector3());
      imported.scale.setScalar(1.92 / Math.max(size.y, .01));
      imported.updateMatrixWorld(true);
      const scaledBounds = new THREE.Box3().setFromObject(imported);
      const center = scaledBounds.getCenter(new THREE.Vector3());
      imported.position.set(-center.x, -scaledBounds.min.y, 0);
      imported.rotation.y = 0;
      bodySocket.add(imported);
      importedLoaded = true;
      imported.visible = true;
      fallback.visible = false;
      // The authored scout replaces the dimensional silhouette; only the route board remains socketed.
      identity.visible = false;
      board.visible = true;
      sling.visible = false;
      mixer = new THREE.AnimationMixer(imported);
      const idle = gltf.animations.find(clip => clip.name === 'Idle') ?? gltf.animations[0];
      if (idle) mixer.clipAction(idle).play();
      receipt = {
        sourceId: 'project-authored-blender-scout',
        usedFallback: false,
        meshCount,
        skinnedMeshCount,
        animationNames: gltf.animations.map(clip => clip.name),
      };
      resolve(receipt);
    }, undefined, () => resolve(receipt));
  });

  let lifecycle: RouteScoutLifecycle = 'active';
  let lastElapsedMs = 0;
  let cue: RouteCue = { id: 'cinderwell-route-07', pose: 'signal', worldPosition: [-2.55, 0, -4.15], worldYaw: 0, boardVisible: true, routeLineState: 'active' };
  const apply = (snapshot: ActorSnapshot) => {
    lifecycle = snapshot.lifecycle;
    const deltaSeconds = Math.max(0, snapshot.elapsedMs - lastElapsedMs) / 1000;
    lastElapsedMs = snapshot.elapsedMs;
    mixer?.update(deltaSeconds);
    root.position.copy(snapshot.worldPosition).add(new THREE.Vector3(.12, -.12, .18));
    root.rotation.y = snapshot.worldYaw;
    root.visible = lifecycle !== 'dead';
    // The authored board is integral to the imported scout assembly; terminal actor lifecycle
    // owns its visibility. The procedural board remains only for the dimensional load fallback.
    bodySocket.visible = true;
    fallback.visible = !importedLoaded;
    identity.visible = !importedLoaded;
    sling.visible = !importedLoaded;
    board.visible = cue.boardVisible;

    routeMaterial.emissiveIntensity = cue.routeLineState === 'active' ? .78 : .18;
    const motion = snapshot.reducedMotion ? 0 : Math.sin(snapshot.elapsedMs / 1050) * .025;
    mask.rotation.y = motion;
    mantle.rotation.z = lifecycle === 'hit' ? -.12 : lifecycle === 'dying' ? -.55 : motion * .35;
    identity.rotation.x = cue.pose === 'signal' ? -.035 : cue.pose === 'depart' ? .05 : 0;
    board.rotation.y = cue.pose === 'signal' ? -.08 : .08;
    groundContact.visible = lifecycle !== 'dead';
    groundContact.scale.set(lifecycle === 'hit' ? 1.08 : 1, 1, 1);
    routeLine.visible = cue.boardVisible;
  };

  const reset = () => {
    lifecycle = 'active';
    lastElapsedMs = 0;
    mixer?.setTime(0);
    root.visible = true;
    identity.rotation.set(0, 0, 0);
    mantle.rotation.set(0, 0, 0);
    mask.rotation.set(0, 0, 0);
    groundContact.visible = true;
  };

  return {
    id: 'route-scout',
    root,
    ready,
    apply,
    setRouteCue: (nextCue) => { cue = nextCue; },
    reset,
    dispose: () => {
      disposed = true;
      root.traverse((item) => {
        if (!(item instanceof THREE.Mesh)) return;
        item.geometry.dispose();
        const materials = Array.isArray(item.material) ? item.material : [item.material];
        materials.forEach(actorMaterial => actorMaterial.dispose());
      });
      root.removeFromParent();
    },
    inspect: () => {
      let castShadowMeshes = 0;
      let receiveShadowMeshes = 0;
      root.traverse((item) => {
        if (!(item instanceof THREE.Mesh) || !item.visible) return;
        if (item.castShadow) castShadowMeshes += 1;
        if (item.receiveShadow) receiveShadowMeshes += 1;
      });
      return {
        presentation: 'integrated-mesh',
        visibleFullActorPlanes: 0,
        castShadowMeshes,
        receiveShadowMeshes,
        hasGroundContact: true,
        materialProfile: 'arcos-painterly-matte',
        boardPresentation: 'world-route-markers',
      };
    },
  };
}
